import { fieldConfig } from "@autoform/zod";
import _ from "lodash";
import { z } from "zod";

/**
 * Opening hours + wheelchair accessibility on a venue (`VenueOpeningHoursDbDto` /
 * `VenueAccessibilityDbDto`). TINA fills both from Google Places and refreshes
 * them every 90 days, but only while `source` is `google`: any edit saved here
 * is stamped `curator` by the API, and TINA never overwrites it again. Emptying
 * every opening span hands the hours back to TINA.
 *
 * The API owns `source` and `updatedAt`; the form never sends them.
 */

export const OPENING_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const ACCESSIBILITY_KEYS = [
  "wheelchairEntrance",
  "wheelchairSeating",
  "wheelchairParking",
  "wheelchairRestroom",
] as const;

type AccessibilityKey = (typeof ACCESSIBILITY_KEYS)[number];

/** A nullable bool as a select: `unknown` is null, and is never sent as "no". */
export const ACCESS_CHOICES = ["unknown", "yes", "no"] as const;
type AccessChoice = (typeof ACCESS_CHOICES)[number];

const OPEN_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const CLOSE_TIME = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;

/** Lenient READ shapes: a stored value is shown, never rejected. */
export const openingHoursReadSchema = z
  .object({
    periods: z
      .array(
        z
          .object({
            day: z.string().nullish(),
            open: z.string().nullish(),
            close: z.string().nullish(),
          })
          .passthrough(),
      )
      .nullish(),
    source: z.string().nullish(),
    updatedAt: z.string().nullish(),
  })
  .passthrough();

export const accessibilityReadSchema = z
  .object({
    wheelchairEntrance: z.boolean().nullish(),
    wheelchairSeating: z.boolean().nullish(),
    wheelchairParking: z.boolean().nullish(),
    wheelchairRestroom: z.boolean().nullish(),
    source: z.string().nullish(),
    updatedAt: z.string().nullish(),
  })
  .passthrough();

type OpeningHoursRead = z.infer<typeof openingHoursReadSchema>;
type AccessibilityRead = z.infer<typeof accessibilityReadSchema>;

const ACCESS_LABELS: Record<AccessibilityKey, string> = {
  wheelchairEntrance: "Wheelchair-accessible entrance",
  wheelchairSeating: "Wheelchair-accessible seating",
  wheelchairParking: "Wheelchair-accessible parking",
  wheelchairRestroom: "Wheelchair-accessible restroom",
};

const accessChoice = (key: AccessibilityKey) =>
  z
    .enum(ACCESS_CHOICES)
    .default("unknown")
    .superRefine(
      fieldConfig({ fieldType: "select", label: ACCESS_LABELS[key] }),
    );

/** Strict FORM shapes. */
export const openingHoursFormSchema = z
  .object({
    periods: z
      .array(
        z.object({
          day: z
            .enum(OPENING_DAYS)
            .superRefine(fieldConfig({ fieldType: "select", label: "Day" })),
          open: z
            .string()
            .regex(OPEN_TIME, "Use HH:MM, 00:00 to 23:59")
            .superRefine(fieldConfig({ label: "Opens (HH:MM)" })),
          close: z
            .string()
            .regex(CLOSE_TIME, "Use HH:MM, 00:00 to 24:00")
            .superRefine(
              fieldConfig({
                label: "Closes (HH:MM)",
                description:
                  "At or before the opening time means the next day.",
              }),
            ),
        }),
      )
      .default([]),
  })
  .superRefine(
    fieldConfig({
      label: "Opening hours",
      description:
        "Filled by TINA from Google. Saving a change marks the hours as yours, and TINA stops updating them; remove every span to hand them back to TINA.",
    }),
  );

export const accessibilityFormSchema = z
  .object({
    wheelchairEntrance: accessChoice("wheelchairEntrance"),
    wheelchairSeating: accessChoice("wheelchairSeating"),
    wheelchairParking: accessChoice("wheelchairParking"),
    wheelchairRestroom: accessChoice("wheelchairRestroom"),
  })
  .superRefine(
    fieldConfig({
      label: "Accessibility",
      description:
        "Filled by TINA from Google. Unknown is not No. Saving a change marks these as yours, and TINA stops updating them.",
    }),
  );

export type OpeningHoursForm = z.infer<typeof openingHoursFormSchema>;
export type AccessibilityForm = z.infer<typeof accessibilityFormSchema>;

export type FactsForm = {
  openingHours: OpeningHoursForm;
  accessibility: AccessibilityForm;
};

const toChoice = (value: boolean | null | undefined): AccessChoice =>
  value === true ? "yes" : value === false ? "no" : "unknown";

const fromChoice = (value: unknown): boolean | null =>
  value === "yes" ? true : value === "no" ? false : null;

const isDay = (day: unknown): day is (typeof OPENING_DAYS)[number] =>
  (OPENING_DAYS as readonly unknown[]).includes(day);

/** The stored facts as form defaults. A legacy or empty value is no spans and all-unknown. */
export function toFactsForm(venue: {
  openingHours?: OpeningHoursRead | null;
  accessibility?: AccessibilityRead | null;
}): FactsForm {
  const periods = (venue.openingHours?.periods ?? []).flatMap((p) =>
    isDay(p.day) && p.open && p.close
      ? [{ day: p.day, open: p.open, close: p.close }]
      : [],
  );
  const access = venue.accessibility ?? {};
  return {
    openingHours: { periods },
    accessibility: {
      wheelchairEntrance: toChoice(access.wheelchairEntrance),
      wheelchairSeating: toChoice(access.wheelchairSeating),
      wheelchairParking: toChoice(access.wheelchairParking),
      wheelchairRestroom: toChoice(access.wheelchairRestroom),
    },
  };
}

export type FactsPayload = {
  openingHours?: {
    periods: { day: string; open: string; close: string }[];
  };
  accessibility?: Record<AccessibilityKey, boolean | null>;
};

/**
 * What the PUT must carry for the facts, from the RAW form values.
 *
 * Raw, not AutoForm's submitted values: `removeEmptyValues` deletes an emptied
 * span list (see `withClearedFields`), so "the curator removed every span" can
 * only be read off `form.getValues()`. Only a REAL change is sent, because the
 * API marks whatever it receives as the curator's.
 */
export function factsPayload(
  raw: Partial<Record<keyof FactsForm, unknown>>,
  defaults: FactsForm,
): FactsPayload {
  const payload: FactsPayload = {};
  const rawHours = raw.openingHours as Partial<OpeningHoursForm> | undefined;
  const periods = (rawHours?.periods ?? []).map((p) => ({
    day: p.day,
    open: p.open,
    close: p.close,
  }));
  if (!_.isEqual(periods, defaults.openingHours.periods)) {
    payload.openingHours = { periods };
  }
  const rawAccess = (raw.accessibility ?? {}) as Partial<AccessibilityForm>;
  const changed = ACCESSIBILITY_KEYS.some(
    (key) => (rawAccess[key] ?? "unknown") !== defaults.accessibility[key],
  );
  if (changed) {
    payload.accessibility = Object.fromEntries(
      ACCESSIBILITY_KEYS.map((key) => [key, fromChoice(rawAccess[key])]),
    ) as Record<AccessibilityKey, boolean | null>;
  }
  return payload;
}
